import { useState } from 'react'
import { FormDialog } from '@/components/common'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { formProblem } from '@/lib/api/fieldErrors'
import { useSettingsMutations } from '../api'
import type { NotificationChannelView } from '../types'

interface Draft {
  enabled: boolean
  ownSmtp: boolean
  smtpHost: string
  smtpPort: string
  smtpSecurity: string
  username: string
  password: string
  fromName: string
  fromAddress: string
  replyTo: string
}

function draftOf(row: NotificationChannelView | undefined): Draft {
  return {
    enabled: row?.enabled ?? false,
    ownSmtp: (row?.provider ?? '').toUpperCase() === 'SMTP',
    smtpHost: row?.smtpHost ?? '',
    smtpPort: row?.smtpPort == null ? '587' : String(row.smtpPort),
    smtpSecurity: row?.smtpSecurity ?? 'STARTTLS',
    username: row?.providerAccount ?? '',
    password: '',
    fromName: row?.fromName ?? '',
    fromAddress: row?.fromAddress ?? '',
    replyTo: row?.replyTo ?? '',
  }
}

/** Settings that work for the providers people ask about most. */
const PRESETS = [
  { name: 'Microsoft 365', host: 'smtp.office365.com', port: '587', security: 'STARTTLS' },
  { name: 'Google Workspace', host: 'smtp.gmail.com', port: '587', security: 'STARTTLS' },
  { name: 'Zoho Mail', host: 'smtp.zoho.com', port: '587', security: 'STARTTLS' },
] as const

/**
 * The email channel: whether this company sends through its own mail server, and which one.
 *
 * <p>Its own form, not the generic channel one, because every other channel's fields are
 * inert configuration and these are a live connection — a host somebody mistypes is every
 * invitation and expiry alert this company sends going nowhere. With "own server" off, mail
 * goes through the HUMIS default sender, and the form says so rather than leaving the fields
 * empty to imply nothing sends.
 *
 * <p>The password is write-only. The server says whether one is stored; this screen never
 * sees it, and a blank box on save keeps whatever is there.
 */
export function EmailChannelDialog({
  row, open, onOpenChange,
}: {
  row: NotificationChannelView | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const actions = useSettingsMutations()
  const [draft, setDraft] = useState<Draft>(() => draftOf(row))
  const [fields, setFields] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }))
  }

  function fieldError(...names: string[]) {
    return names.map((name) => fields[name]).find(Boolean)
  }

  async function save() {
    setError(null)
    setFields({})
    try {
      await actions.saveChannel.mutateAsync({
        channel: 'EMAIL',
        enabled: draft.enabled,
        provider: draft.ownSmtp ? 'SMTP' : null,
        fromName: draft.fromName || null,
        fromAddress: draft.fromAddress || null,
        replyTo: draft.replyTo || null,
        smtpHost: draft.ownSmtp ? draft.smtpHost || null : null,
        smtpPort: draft.ownSmtp && draft.smtpPort ? Number(draft.smtpPort) : null,
        smtpSecurity: draft.ownSmtp ? draft.smtpSecurity || null : null,
        providerAccount: draft.ownSmtp ? draft.username || null : null,
        // Blank keeps what is stored — the server never sends it back to be re-sent.
        providerSecret: draft.ownSmtp && draft.password ? draft.password : null,
        note: row?.note ?? null,
      })
      onOpenChange(false)
    } catch (caught) {
      const problem = formProblem(caught, 'The email settings could not be saved.')
      setFields(problem.fields)
      setError(problem.summary)
    }
  }

  const text = (
    id: string, label: string, key: keyof Draft, errorFor: string[],
    extra?: { type?: string; placeholder?: string; hint?: string; autoComplete?: string },
  ) => {
    const message = fieldError(...errorFor)
    return (
      <div>
        <Label htmlFor={id}>{label}</Label>
        <Input
          id={id}
          type={extra?.type ?? 'text'}
          autoComplete={extra?.autoComplete}
          value={String(draft[key])}
          placeholder={extra?.placeholder}
          invalid={Boolean(message)}
          aria-describedby={message ? `${id}-error` : undefined}
          onChange={(event) => set(key, event.target.value as never)}
        />
        {message
          ? <p id={`${id}-error`} role="alert" className="mt-1 text-xs text-[var(--color-danger)]">{message}</p>
          : extra?.hint && <p className="mt-1 text-xs text-[var(--color-muted)]">{extra.hint}</p>}
      </div>
    )
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) { setError(null); setFields({}) }
        onOpenChange(next)
      }}
      title="Email"
      description="Invitations, alerts and notifications are sent by email from here."
      size="lg"
      submitLabel="Save"
      loading={actions.saveChannel.isPending}
      error={error}
      onSubmit={() => { void save() }}
    >
      <div className="space-y-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" className="size-4" checked={draft.enabled}
            onChange={(event) => set('enabled', event.target.checked)}
          />
          Send email
        </label>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox" className="mt-0.5 size-4" checked={draft.ownSmtp}
            onChange={(event) => set('ownSmtp', event.target.checked)}
          />
          <span>
            Use our own mail server (SMTP)
            <span className="block text-xs text-[var(--color-muted)]">
              {draft.ownSmtp
                ? 'Mail goes out from your server and your address, so it reaches people from a domain they know.'
                : row?.platformDefaultConfigured === false
                  ? 'Using the HUMIS default sender — which is not set up on this installation, so nothing is sent until you add your own server.'
                  : 'Using the HUMIS default sender.'}
            </span>
          </span>
        </label>

        {draft.ownSmtp && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                {text('smtp-host', 'Server', 'smtpHost', ['smtpHost'], { placeholder: 'smtp.office365.com' })}
              </div>
              {text('smtp-port', 'Port', 'smtpPort', ['smtpPort'], { type: 'number', placeholder: '587' })}
            </div>
            <div>
              <Label htmlFor="smtp-security">Security</Label>
              <Select
                id="smtp-security" className="w-full" value={draft.smtpSecurity}
                aria-invalid={Boolean(fieldError('smtpSecurity'))}
                onChange={(event) => set('smtpSecurity', event.target.value)}
              >
                <option value="STARTTLS">STARTTLS (port 587)</option>
                <option value="SSL_TLS">SSL/TLS (port 465)</option>
                <option value="NONE">None — only for a relay on a private network</option>
              </Select>
              {fieldError('smtpSecurity') && (
                <p role="alert" className="mt-1 text-xs text-[var(--color-danger)]">{fieldError('smtpSecurity')}</p>
              )}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {text('smtp-username', 'Username', 'username', ['providerAccount'], { autoComplete: 'off' })}
              {text('smtp-password', 'Password', 'password', ['providerSecret'], {
                type: 'password',
                autoComplete: 'new-password',
                placeholder: row?.passwordSet ? '•••••• set' : '',
                hint: row?.passwordSet
                  ? 'Leave blank to keep the stored password. It is encrypted and never shown.'
                  : 'Encrypted when saved, and never shown again.',
              })}
            </div>
            <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-2 text-xs text-[var(--color-muted)]">
              Common settings —{' '}
              {PRESETS.map((preset, index) => (
                <span key={preset.name}>
                  {index > 0 && ' · '}
                  <button
                    type="button"
                    className="text-[var(--color-primary)] hover:underline"
                    onClick={() => setDraft((previous) => ({
                      ...previous, smtpHost: preset.host, smtpPort: preset.port, smtpSecurity: preset.security,
                    }))}
                  >
                    {preset.name}
                  </button>{' '}
                  ({preset.host}, {preset.port}, {preset.security})
                </span>
              ))}
              {' · '}Amazon SES (email-smtp.&lt;region&gt;.amazonaws.com, 587, STARTTLS). Google and
              Microsoft accounts with two-step sign-in need an app password, not the normal one.
            </p>
          </>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          {text('email-from-name', 'From name', 'fromName', ['fromName'], { placeholder: 'Acme HR' })}
          {text('email-from-address', 'From address', 'fromAddress', ['fromAddress'], {
            placeholder: 'hr@yourcompany.com',
            hint: draft.ownSmtp
              ? 'An address your server lets this account send as.'
              : 'Shown for reference. The HUMIS sender uses its own address.',
          })}
        </div>
        {text('email-reply-to', 'Reply-to', 'replyTo', ['replyTo'], {
          placeholder: 'Optional — where replies should go',
        })}
      </div>
    </FormDialog>
  )
}
